import React, { useEffect, useState, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { ExternalLink, Save, Images, CalendarDays, Loader2, ArrowUp, ArrowDown, Palette, Search, Upload, X } from 'lucide-react';
import api from '../lib/api';
import { PageHeader } from '../components/layout/PageHeader';
import { PhotoManager } from '../components/landing/PhotoManager';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Sun, Moon } from 'lucide-react';
import { FONT_THEMES, ensureAllFontsLoaded } from '../lib/fontThemes';

const HEX_RE = /^#([0-9a-fA-F]{6})$/;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_SECTIONS = [
  { id: 'events', visible: true },
  { id: 'gallery', visible: true },
  { id: 'about', visible: true },
];
const SECTION_LABELS = {
  events: { name: 'Featured events', desc: 'Grid of events you’ve toggled to feature' },
  gallery: { name: 'Highlights carousel', desc: 'Your uploaded highlight photos' },
  about: { name: 'About section', desc: 'Your “About” text + contact links' },
};

export default function LandingManager() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [events, setEvents] = useState([]);
  const [ogUploading, setOgUploading] = useState(false);
  const ogFileRef = useRef(null);

  const loadEvents = useCallback(() => {
    api.get('/events').then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    ensureAllFontsLoaded();
    Promise.all([api.get('/landing'), api.get('/events')])
      .then(([c, e]) => { setCfg(c.data); setEvents(e.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const sections = cfg?.sections?.length ? cfg.sections : DEFAULT_SECTIONS;
  const moveSection = (idx, dir) => {
    const next = [...sections];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    set('sections', next);
  };
  const toggleSection = (idx) => {
    const next = sections.map((s, i) => (i === idx ? { ...s, visible: !s.visible } : s));
    set('sections', next);
  };

  const uploadOg = async (file) => {
    if (!file) return;
    setOgUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/landing/upload-image', fd);
      set('og_image_url', r.data.url);
      toast.success('Social image uploaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setOgUploading(false);
      if (ogFileRef.current) ogFileRef.current.value = '';
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/landing', {
        hero_title: cfg.hero_title, hero_subtitle: cfg.hero_subtitle,
        about: cfg.about || '', instagram: cfg.instagram || '', contact_email: cfg.contact_email || '',
        default_theme: cfg.default_theme || 'light',
        font_theme: cfg.font_theme || 'editorial',
        sections: cfg.sections || DEFAULT_SECTIONS,
        brand_primary: cfg.brand_primary || '',
        brand_accent: cfg.brand_accent || '',
        seo_title: cfg.seo_title || '',
        seo_description: cfg.seo_description || '',
        og_image_url: cfg.og_image_url || '',
      });
      toast.success('Landing page updated');
    } catch (e) { toast.error('Could not save'); }
    finally { setSaving(false); }
  };

  const toggleFeatured = async (ev, value) => {
    setEvents((list) => list.map((x) => (x.id === ev.id ? { ...x, show_on_landing: value } : x)));
    try { await api.put(`/events/${ev.id}`, { show_on_landing: value }); }
    catch (e) { toast.error('Could not update'); loadEvents(); }
  };

  if (loading) return <div className="space-y-4"><Skeleton className="h-10 w-56" /><Skeleton className="h-96 rounded-2xl" /></div>;

  return (
    <div data-testid="landing-manager-page">
      <PageHeader title="Landing Page" subtitle="Curate your public homepage — hero text, featured events, and photo highlights.">
        <a href="/" target="_blank" rel="noreferrer"><Button variant="secondary" className="gap-2" data-testid="view-live-landing"><ExternalLink className="h-4 w-4" /> View live</Button></a>
      </PageHeader>

      <Tabs defaultValue="hero">
        <TabsList>
          <TabsTrigger value="hero" data-testid="landing-tab-hero">Hero &amp; About</TabsTrigger>
          <TabsTrigger value="events" data-testid="landing-tab-events">Featured events</TabsTrigger>
          <TabsTrigger value="photos" data-testid="landing-tab-photos">Highlights</TabsTrigger>
          <TabsTrigger value="branding" data-testid="landing-tab-branding">Branding &amp; SEO</TabsTrigger>
        </TabsList>

        <TabsContent value="hero" className="mt-6">
          <div className="rounded-2xl border border-border bg-card p-6 max-w-2xl space-y-4">
            <div className="space-y-1.5"><Label>Hero headline</Label><Input value={cfg.hero_title || ''} onChange={(e) => set('hero_title', e.target.value)} className="h-11" data-testid="hero-title-input" /></div>
            <div className="space-y-1.5"><Label>Tagline / subtitle</Label><Textarea value={cfg.hero_subtitle || ''} onChange={(e) => set('hero_subtitle', e.target.value)} rows={2} data-testid="hero-subtitle-input" /></div>
            <div className="space-y-1.5"><Label>About Thrive Gatherings (optional)</Label><Textarea value={cfg.about || ''} onChange={(e) => set('about', e.target.value)} rows={4} placeholder="Tell visitors who you are…" data-testid="about-input" /></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Instagram URL (optional)</Label><Input value={cfg.instagram || ''} onChange={(e) => set('instagram', e.target.value)} placeholder="https://instagram.com/…" className="h-11" /></div>
              <div className="space-y-1.5"><Label>Contact email (optional)</Label><Input value={cfg.contact_email || ''} onChange={(e) => set('contact_email', e.target.value)} placeholder="hello@thrivegathering.life" className="h-11" /></div>
            </div>
            <div className="space-y-2 pt-1">
              <Label>Default theme for visitors</Label>
              <p className="text-xs text-muted-foreground -mt-1">The look visitors see first. They can still toggle light/dark themselves.</p>
              <div className="inline-flex rounded-xl border border-border p-1 bg-secondary/50" data-testid="default-theme-toggle">
                {[{ v: 'light', l: 'Light', icon: Sun }, { v: 'dark', l: 'Dark', icon: Moon }].map((opt) => {
                  const Icon = opt.icon;
                  const active = (cfg.default_theme || 'light') === opt.v;
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => set('default_theme', opt.v)}
                      className={`inline-flex items-center gap-2 px-4 h-9 rounded-lg text-sm font-medium transition-colors ${active ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                      data-testid={`default-theme-${opt.v}`}
                    >
                      <Icon className="h-4 w-4" /> {opt.l}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="space-y-2 pt-1">
              <Label>Landing page font</Label>
              <p className="text-xs text-muted-foreground -mt-1">Sets the typography for your public homepage.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-testid="landing-font-grid">
                {FONT_THEMES.map((t) => {
                  const selected = (cfg.font_theme || 'editorial') === t.id;
                  return (
                    <button key={t.id} type="button" onClick={() => set('font_theme', t.id)}
                      className={`text-left rounded-xl border-2 p-3.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${selected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'}`}
                      aria-pressed={selected} data-testid={`landing-font-${t.id}`}>
                      <div className="text-lg leading-none text-foreground" style={{ fontFamily: t.display }}>{t.name}</div>
                      <div className="mt-1.5 text-xs text-muted-foreground" style={{ fontFamily: t.body }}>Where good people gather.</div>
                    </button>
                  );
                })}
              </div>
            </div>
            <Button onClick={save} disabled={saving} className="gap-2 h-11" data-testid="save-landing-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="events" className="mt-6">
          <div className="rounded-2xl border border-border bg-card overflow-hidden max-w-3xl">
            <div className="p-4 border-b border-border"><p className="text-sm text-muted-foreground">Toggle which events appear on your public landing page.</p></div>
            {events.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">No events yet. Create one to feature it here.</div>
            ) : events.map((ev) => (
              <div key={ev.id} className="flex items-center gap-3 px-4 py-3 border-b border-border last:border-0" data-testid="featured-event-row">
                <div className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: (ev.theme_color || '#2F6B57') + '20' }}><CalendarDays className="h-4 w-4" style={{ color: ev.theme_color || '#2F6B57' }} /></div>
                <div className="min-w-0 flex-1"><div className="text-sm font-medium truncate">{ev.title}</div><div className="text-xs text-muted-foreground">{ev.date || 'No date'}</div></div>
                <Switch checked={!!ev.show_on_landing} onCheckedChange={(v) => toggleFeatured(ev, v)} data-testid="featured-toggle" />
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="photos" className="mt-6">
          <div className="rounded-2xl border border-border bg-card p-6">
            <PhotoManager scope="site" title="Highlights carousel" hint="These photos appear in the highlights carousel on your public landing page. Drag isn't needed — use the arrows to reorder." />
          </div>
        </TabsContent>

        <TabsContent value="branding" className="mt-6">
          <div className="max-w-2xl space-y-6">
            {/* Section order & visibility */}
            <div className="rounded-2xl border border-border bg-card p-6">
              <h2 className="font-display text-xl font-semibold mb-1">Page sections</h2>
              <p className="text-sm text-muted-foreground mb-4">Reorder or hide sections of your public homepage. The hero header and footer always stay.</p>
              <div className="space-y-2" data-testid="landing-sections-list">
                {sections.map((s, idx) => {
                  const meta = SECTION_LABELS[s.id] || { name: s.id, desc: '' };
                  return (
                    <div key={s.id} className="flex items-center gap-3 rounded-xl border border-border bg-background p-3" data-testid={`landing-section-${s.id}`}>
                      <div className="flex flex-col">
                        <button type="button" onClick={() => moveSection(idx, -1)} disabled={idx === 0}
                          className="text-muted-foreground hover:text-foreground disabled:opacity-30" aria-label="Move up" data-testid={`section-up-${s.id}`}>
                          <ArrowUp className="h-4 w-4" />
                        </button>
                        <button type="button" onClick={() => moveSection(idx, 1)} disabled={idx === sections.length - 1}
                          className="text-muted-foreground hover:text-foreground disabled:opacity-30" aria-label="Move down" data-testid={`section-down-${s.id}`}>
                          <ArrowDown className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium">{meta.name}</div>
                        <div className="text-xs text-muted-foreground">{meta.desc}</div>
                      </div>
                      <Switch checked={!!s.visible} onCheckedChange={() => toggleSection(idx)} data-testid={`section-visible-${s.id}`} />
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Brand colors */}
            <div className="rounded-2xl border border-border bg-card p-6">
              <h2 className="font-display text-xl font-semibold mb-1 flex items-center gap-2"><Palette className="h-5 w-5 text-primary" /> Brand colors</h2>
              <p className="text-sm text-muted-foreground mb-4">Customize the accent colors of your public homepage. Leave blank to use the default Thrive palette.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Primary color</Label>
                  <div className="flex gap-2">
                    <input type="color" value={HEX_RE.test(cfg.brand_primary) ? cfg.brand_primary : '#2F6B57'}
                      onChange={(e) => set('brand_primary', e.target.value)}
                      className="h-11 w-12 rounded-lg border border-border bg-card cursor-pointer p-1" aria-label="Primary color" />
                    <Input value={cfg.brand_primary || ''} onChange={(e) => set('brand_primary', e.target.value)} placeholder="Default" className="h-11 font-mono text-sm" data-testid="brand-primary-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Accent color</Label>
                  <div className="flex gap-2">
                    <input type="color" value={HEX_RE.test(cfg.brand_accent) ? cfg.brand_accent : '#C86B4A'}
                      onChange={(e) => set('brand_accent', e.target.value)}
                      className="h-11 w-12 rounded-lg border border-border bg-card cursor-pointer p-1" aria-label="Accent color" />
                    <Input value={cfg.brand_accent || ''} onChange={(e) => set('brand_accent', e.target.value)} placeholder="Default" className="h-11 font-mono text-sm" data-testid="brand-accent-input" />
                  </div>
                </div>
              </div>
              {(cfg.brand_primary || cfg.brand_accent) && (
                <Button variant="ghost" size="sm" className="mt-3 text-muted-foreground" onClick={() => { set('brand_primary', ''); set('brand_accent', ''); }}>
                  Reset to default palette
                </Button>
              )}
            </div>

            {/* SEO / social */}
            <div className="rounded-2xl border border-border bg-card p-6">
              <h2 className="font-display text-xl font-semibold mb-1 flex items-center gap-2"><Search className="h-5 w-5 text-primary" /> SEO &amp; social preview</h2>
              <p className="text-sm text-muted-foreground mb-4">Controls the browser tab title, search description, and the image shown when your homepage link is shared.</p>
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label>Page title</Label>
                  <Input value={cfg.seo_title || ''} onChange={(e) => set('seo_title', e.target.value)} placeholder="Thrive Gatherings" className="h-11" data-testid="seo-title-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>Meta description</Label>
                  <Textarea value={cfg.seo_description || ''} onChange={(e) => set('seo_description', e.target.value)} rows={2} placeholder="A short sentence describing your gatherings…" data-testid="seo-description-input" />
                </div>
                <div className="space-y-2">
                  <Label>Social preview image <span className="text-muted-foreground font-normal">(1200×630 recommended)</span></Label>
                  <div className="flex items-center gap-3">
                    {cfg.og_image_url ? (
                      <div className="relative h-16 w-28 rounded-lg border border-border bg-card overflow-hidden flex items-center justify-center">
                        <img src={`${BACKEND}${cfg.og_image_url}`} alt="social preview" className="max-h-full max-w-full object-cover" />
                        <button type="button" onClick={() => set('og_image_url', '')}
                          className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-destructive text-white flex items-center justify-center" aria-label="Remove image">
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ) : (
                      <div className="h-16 w-28 rounded-lg border border-dashed border-border flex items-center justify-center text-muted-foreground text-xs">No image</div>
                    )}
                    <Button type="button" variant="outline" size="sm" className="gap-2" disabled={ogUploading}
                      onClick={() => ogFileRef.current?.click()} data-testid="og-image-upload">
                      {ogUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                      {cfg.og_image_url ? 'Replace image' : 'Upload image'}
                    </Button>
                    <input ref={ogFileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                      onChange={(e) => uploadOg(e.target.files?.[0])} />
                  </div>
                  <p className="text-xs text-muted-foreground">Note: rich link previews (Facebook, iMessage) work best once your custom domain is connected.</p>
                </div>
              </div>
            </div>

            <Button onClick={save} disabled={saving} className="gap-2 h-11" data-testid="save-branding-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
